import { forwardRef, useLayoutEffect, useRef, type ReactNode } from 'react';
import styles from './PaymentResultIcon.module.css';

const RADIUS = 14;
const CIRCUMFERENCE = 2 * Math.PI * RADIUS;
const LOADING_ARC = CIRCUMFERENCE * 0.28;

export interface PaymentResultIconProps {
	icon?: ReactNode;
	tone?: 'success' | 'error';
	spinning?: boolean;
	bare?: boolean;
	ringed?: boolean;
	animateReveal?: boolean;
}

function currentRotationDeg(el: SVGSVGElement): number {
	const transform = getComputedStyle(el).transform;
	if (!transform || transform === 'none') return 0;
	const match = /matrix\(([^)]+)\)/.exec(transform);
	if (!match) return 0;
	const [a, b] = match[1].split(',').map(Number);
	return (Math.atan2(b, a) * 180) / Math.PI;
}

export const PaymentResultIcon = forwardRef<HTMLDivElement, PaymentResultIconProps>(
	({ icon, tone = 'success', spinning = false, bare = false, ringed = false, animateReveal = true }, ref) => {
		const svgRef = useRef<SVGSVGElement>(null);
		const wasSpinningRef = useRef(spinning);

		useLayoutEffect(() => {
			const svg = svgRef.current;
			if (svg && wasSpinningRef.current && !spinning) {
				const angle = currentRotationDeg(svg);
				svg.style.animation = 'none';
				svg.style.transform = `rotate(${angle}deg)`;
			}
			wasSpinningRef.current = spinning;
		}, [spinning]);

		const bgClass = bare ? `${styles[tone]} ${styles.bare}` : styles[tone];
		const showRing = spinning || ringed;

		return (
			<div ref={ref} className={`${styles.wrapper} ${bgClass}`}>
				{showRing && (
					<svg
						ref={svgRef}
						viewBox="0 0 40 40"
						className={`${styles.ringSvg} ${spinning ? styles.spinningSvg : ''}`}
					>
						<circle
							cx="20"
							cy="20"
							r={RADIUS}
							className={styles.ringCircle}
							style={{ strokeDasharray: `${spinning ? LOADING_ARC : CIRCUMFERENCE} ${CIRCUMFERENCE}` }}
						/>
					</svg>
				)}
				{icon && (
					<span className={`${styles.icon} ${animateReveal ? styles.reveal : ''}`}>{icon}</span>
				)}
			</div>
		);
	}
);

PaymentResultIcon.displayName = 'PaymentResultIcon';
